import { useState, useCallback, useEffect } from 'react';

interface UseEditableTitleProps {
  title?: string;
  isGroup?: boolean;
  groupId?: string;
  onUpdateGroupTitle?: (groupId: string, newTitle: string) => void;
  validateGroupName: (name: string) => string;
}

export const useEditableTitle = ({
  title = '',
  isGroup = false,
  groupId,
  onUpdateGroupTitle,
  validateGroupName,
}: UseEditableTitleProps) => {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState(title);
  const [editTitleError, setEditTitleError] = useState<string>('');

  useEffect(() => {
    setEditTitle(title);
  }, [title]);

  const handleSwitchToEditMode = useCallback(() => {
    setEditTitle(title || '');
    setIsEditingTitle(true);
  }, [title]);

  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEditTitle(e.target.value);
      const error = validateGroupName(e.target.value);
      setEditTitleError(error);
    },
    [validateGroupName],
  );

  const handleTitleSave = useCallback(() => {
    const error = validateGroupName(editTitle);
    setEditTitleError(error);
    if (error) {
      return;
    }
    if (
      isGroup &&
      groupId &&
      editTitle.trim() &&
      editTitle !== title &&
      onUpdateGroupTitle
    ) {
      onUpdateGroupTitle(groupId, editTitle.trim());
    }
    setIsEditingTitle(false);
  }, [
    editTitle,
    groupId,
    isGroup,
    onUpdateGroupTitle,
    title,
    validateGroupName,
  ]);

  return {
    isEditingTitle,
    editTitle,
    editTitleError,
    setIsEditingTitle,
    setEditTitle,
    setEditTitleError,
    handleSwitchToEditMode,
    handleTitleChange,
    handleTitleSave,
  };
};
